<template>
  <v-navigation-drawer :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" :rail="rail"
    @update:rail="$emit('update:rail', $event)" :permanent="$vuetify.display.mdAndUp"
    :temporary="$vuetify.display.smAndDown" :width="257" :rail-width="72" color="surface"
    class="border-e sidebar-drawer b-color">
    <!-- Logo Section -->
    <v-img height="60px" v-if="!rail" class="mt-4" eager :src="logo.full" alt="Cliqforms" contain />
    <div v-if="rail" class="mx-auto mt-4">
      <router-link to="/" class="">
        <v-avatar rounded="lg" size="50">
          <v-img eager :src="logo.icon" alt="Cliqforms" height="max-content" aspect-ratio="1/1" cover />
        </v-avatar>
      </router-link>
    </div>
    <v-btn v-if="$vuetify.display.smAndDown" icon variant="flat" rounded="lg" size="40" class="sidebar-icon mx-auto"
      @click="$emit('update:modelValue', false)">
      <v-icon size="20">mdi-close</v-icon>
    </v-btn>
    <!-- Navigation -->
    <div class="pa-3 flex-grow-1 nav-scroll">
      <template v-for="(group, idx) in visibleNavigationGroups" :key="idx">
        <div v-if="!rail && group.title"
          class="text-uppercase text-body-small font-weight-bold text-medium-emphasis mt-4 mb-2 px-3">
          {{ group.title }}
        </div>

        <v-tooltip v-for="item in group.items" :key="item.value" :model-value="isTooltipActive(item.value)"
          @update:model-value="(val) => handleTooltipToggle(val, item.value)"
          :text="getNavTooltipText(item)"
          :location="getNavTooltipLocation(item)" :max-width="item.tooltipLocation === 'right' ? 300 : undefined"
          :disabled="!hasNavTooltip(item)" interactive>
          <template #activator="{ props: tooltipProps }">
            <component :is="getNavItemTag(item)" v-bind="getNavItemWrapperProps(item, tooltipProps)">
              <div class="nav-item d-flex align-center ga-2 px-3 py-2 mb-1 rounded-lg"
                :class="{
                  'nav-item--active': isNavItemActive(item),
                  'nav-item--disabled': item.disabled,
                  'nav-item--loading': isNavItemLoading(item),
                  'cursor-pointer': item.external || item.to || item.action
                }">
              <v-progress-circular v-if="isNavItemLoading(item)" class="nav-progress" size="20" width="2"
                color="brand-color" indeterminate />
              <v-icon v-else class="nav-icon" size="22">{{ item.icon }}</v-icon>
              <span v-if="!rail" class="text-body-medium nav-text" :class="{ 'font-weight-bold': isNavItemActive(item) }">
                {{ isNavItemLoading(item) ? 'Opening eSign' : item.title }}
              </span>
              <v-chip v-if="!rail && item.badge" size="x-small" color="accent-color" variant="flat"
                :class="['ml-auto', 'nav-chip', { 'nav-chip--glow': item.disabled || item.badge === 'Upgrade' }]">
                {{ item.badge }}
              </v-chip>
              </div>
            </component>
          </template>
          <template v-if="item.tooltip && (item.tooltipLocation || 'top') === 'right'" #default>
            <div class="d-flex flex-column justify-start py-3">
              <span class="font-weight-bold text-white">{{ item.title }}</span>
              <span class="font-weight-regular text-white text-body-small py-4">{{ item.tooltip || item.title }}</span>
              <!-- Outlined at rest, filled white on hover (each button tracks its own hover) -->
              <v-hover v-slot="{ isHovering, props: hoverProps }">
                <v-btn v-bind="hoverProps" :variant="isHovering ? 'flat' : 'outlined'" size="small" color="white"
                  class="text-none align-self-start" :href="item.learnMore" target="_blank">
                  Learn More
                </v-btn>
              </v-hover>
            </div>
          </template>
        </v-tooltip>
      </template>
    </div>

    <!-- Bottom Quick Actions -->
    <div class="pa-3 border-t b-color sidebar-footer">
      <div class="d-flex" :class="rail ? 'flex-column align-center ga-2' : 'justify-center ga-3'">
        <v-tooltip text="Quick Start" location="top" :model-value="isTooltipActive('sidebar-quickstart')"
          @update:model-value="(val) => handleTooltipToggle(val, 'sidebar-quickstart')">
          <template #activator="{ props }">
            <router-link :to="{ name: 'dashboard', query: { quickstart: 'video' } }" class="text-decoration-none">
              <v-btn v-bind="props" icon variant="flat" rounded="lg" size="40" class="sidebar-icon">
                <v-icon size="20">mdi-play-circle-outline</v-icon>
              </v-btn>
            </router-link>
          </template>
        </v-tooltip>
        <v-tooltip text="Help" location="top" :model-value="isTooltipActive('sidebar-help')"
          @update:model-value="(val) => handleTooltipToggle(val, 'sidebar-help')">
          <template #activator="{ props }">
            <router-link :to="{ name: 'help.index' }" class="text-decoration-none">
              <v-btn v-bind="props" icon variant="flat" rounded="lg" size="40" class="sidebar-icon">
                <v-icon size="20">mdi-help-circle-outline</v-icon>
              </v-btn>
            </router-link>
          </template>
        </v-tooltip>
        <v-tooltip text="Settings" location="top" :model-value="isTooltipActive('sidebar-settings')"
          @update:model-value="(val) => handleTooltipToggle(val, 'sidebar-settings')">
          <template #activator="{ props }">
            <router-link :to="{ name: 'settings' }" class="text-decoration-none">
              <v-btn v-bind="props" icon variant="flat" rounded="lg" size="40" class="sidebar-icon">
                <v-icon size="20">mdi-cog-outline</v-icon>
              </v-btn>
            </router-link>
          </template>
        </v-tooltip>
        <v-tooltip text="Logout" location="top" :model-value="isTooltipActive('sidebar-logout')"
          @update:model-value="(val) => handleTooltipToggle(val, 'sidebar-logout')">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon variant="outlined" rounded="lg" size="40" color="error"
               @click="handleLogout">
              <v-icon size="20" color="error">mdi-logout</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
      </div>
    </div>
  </v-navigation-drawer>
  <v-snackbar v-model="esignErrorVisible" color="error" timeout="5000">
    {{ esignErrorMessage }}
  </v-snackbar>
</template>

<script>
import { navigationGroups } from '@/config/layout/navigation'
import authMixin from '@/mixins/auth'
import userMixin from '@/mixins/user'
import utilityMixin from '@/mixins/utility'
import { featureSlugs } from '@/shared/constants/packagebuilder/features'
import * as actionTypes from '@/store/action-types'
import { openEsign } from '@/utils/esignSso'
import logoFullSrc from '@/assets/icons/branding/light/logo-new.png'
import logoIconSrc from '@/assets/icons/branding/favicon/light.svg'

export default {
  name: 'Sidebar',
  mixins: [authMixin, userMixin, utilityMixin],
  props: {
    modelValue: { type: Boolean, required: true },
    rail: { type: Boolean, required: true },
  },
  emits: ['update:modelValue', 'update:rail'],
  data: () => ({
    logo: {
      full: logoFullSrc,
      icon: logoIconSrc
    },
    current: {
      normalizedPath: '',
      routeName: ''
    },
    openingEsign: false,
    esignErrorVisible: false,
    esignErrorMessage: '',
  }),
  computed: {
    featureSlugs() {
      return featureSlugs
    },
    // Cache feature access results to avoid repeated lookups
    featureAccessCache() {
      const cache = {}
      Object.keys(this.featureSlugs).forEach(key => {
        cache[key] = this.canUseFeature(this.featureSlugs[key])
      })
      return cache
    },
    visibleNavigationGroups() {
      const isAdmin = this.isAdmin
      const hasSubscription = this.hasSubscription
      const featureCache = this.featureAccessCache

      return navigationGroups.map(group => ({
        ...group,
        items: group.items.filter(item => {
          // Check admin only items
          if (item.adminOnly && !isAdmin) {
            return false
          }
          // Check feature-gated items
          if (item.requiresFeature && !featureCache[item.requiresFeature] && !item.lockWhenMissing) {
            return false
          }
          return true
        }).map(item => {
          if (item.requiresFeature && !featureCache[item.requiresFeature] && item.lockWhenMissing) {
            return {
              ...item,
              disabled: true,
              badge: 'Upgrade',
              tooltip: item.lockedTooltip || `${item.title} is not available on your current plan.`,
              tooltipLocation: 'right'
            }
          }

          if (item.value === 'plans') {
            return {
              ...item,
              badge: hasSubscription || isAdmin ? null : item.badge
            }
          }
          return item
        })
      })).filter(group => group.items.length > 0)
    }
  },
  methods: {
    hasNavTooltip(item) {
      return Boolean(item.tooltip) || this.rail
    },
    getNavTooltipText(item) {
      if ((item.tooltipLocation || 'top') === 'right') {
        return undefined
      }

      return item.tooltip || (this.rail ? item.title : undefined)
    },
    getNavTooltipLocation(item) {
      if (item.tooltip) {
        return item.tooltipLocation || 'top'
      }

      return this.rail ? 'right' : 'top'
    },
    getNavItemTag(item) {
      if (item.action) return 'button'
      if (item.external) return 'a'
      // A router-link always navigates on click regardless of item.disabled (unlike the
      // action branch below, which checks item.disabled itself in handleNavAction) - render
      // a disabled item as inert instead so the lock isn't just cosmetic.
      if (item.to && !item.disabled) return 'router-link'
      return 'div'
    },
    getNavItemWrapperProps(item, tooltipProps) {
      if (item.action) {
        return {
          ...tooltipProps,
          type: 'button',
          'aria-disabled': item.disabled || this.isNavItemLoading(item) ? 'true' : undefined,
          'aria-busy': this.isNavItemLoading(item) ? 'true' : undefined,
          class: 'nav-link-wrapper nav-link-wrapper--button',
          onClick: () => this.handleNavAction(item)
        }
      }

      if (item.external) {
        return {
          ...tooltipProps,
          href: item.href,
          target: '_blank',
          class: 'nav-link-wrapper'
        }
      }

      if (item.to && !item.disabled) {
        return {
          ...tooltipProps,
          to: item.to,
          class: 'nav-link-wrapper'
        }
      }

      return tooltipProps
    },
    normalizePath(path) {
      return (path !== '/' && path.endsWith('/')) ? path.slice(0, -1) : path
    },
    isNavItemActive(item) {
      if (!item.to) return false
      const targetPath = this.normalizePath(this.$router.resolve(item.to).path)
      const currentPath = this.current.normalizedPath
      const currentName = this.current.routeName

      if (item.to.name === 'dashboard') {
        return currentName === 'dashboard' || currentPath === targetPath
      }

      if (currentName && item.to.name && item.to.name.includes('.')) {
        const baseName = item.to.name.split('.')[0]
        if (currentName === item.to.name || currentName.startsWith(`${baseName}.`)) {
          return true
        }
      }

      return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`)
    },
    canUseFeature(featureSlug) {
      return this.userMixin_hasFeatureAccess(featureSlug)
    },
    isNavItemLoading(item) {
      return item.action === 'openEsign' && this.openingEsign
    },
    handleLogout() {
      this.$store.dispatch(actionTypes.LOGOUT_USER).then(() => {
        this.$router.push({ name: 'login' })
      })
    },
    handleNavAction(item) {
      if (item.disabled || this.isNavItemLoading(item)) {
        return
      }

      if (item.action === 'openEsign') {
        this.openingEsign = true
        openEsign('/dashboard')
          .catch((error) => {
            console.error('Failed to open eSign:', error)
            this.esignErrorMessage = error?.response?.data?.message || error?.message || 'Unable to open eSign. Please try again.'
            this.esignErrorVisible = true
            this.openingEsign = false
          })
      }
    },
  },
  watch: {
    '$route': {
      immediate: true,
      handler(newRoute) {
        this.current.normalizedPath = this.normalizePath(newRoute.path)
        this.current.routeName = newRoute.name
      }
    }
  }
}
</script>

<style scoped>
.sidebar-drawer {
  background: var(--surface-color) !important;
}

.sidebar-drawer :deep(.v-navigation-drawer__content) {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.nav-scroll {
  overflow-y: auto;
}

.nav-icon {
  color: rgb(var(--brand-color-rgb));
  transition: color 0.2s ease;
}

.nav-link-wrapper {
  text-decoration: none;
  color: inherit;
}

.nav-link-wrapper--button {
  appearance: none;
  background: transparent;
  border: 0;
  display: block;
  padding: 0;
  text-align: left;
  width: 100%;
}

.nav-link-wrapper--button[aria-disabled="true"] {
  cursor: not-allowed;
}

.nav-item {
  transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

.nav-item--disabled .nav-icon,
.nav-item--disabled .nav-text {
  opacity: 0.75;
}

.nav-item--disabled {
  cursor: not-allowed;
}

.nav-item--loading {
  pointer-events: none;
}

.nav-progress {
  flex: 0 0 22px;
}

.nav-item:not(.nav-item--active):hover {
  background: rgba(var(--brand-color-rgb), 0.16);
  color: var(--text-primary);
  box-shadow: 0 1px 3px rgba(var(--brand-color-rgb), 0.08);
}

.nav-item:not(.nav-item--active):hover .nav-text {
  color: var(--text-primary);
}

.nav-item:not(.nav-item--active):hover :deep(.v-icon) {
  color: rgb(var(--brand-color-rgb)) !important;
}

.nav-item--active {
  background: rgba(var(--brand-color-rgb), 0.14);
  color: var(--brand-color-dark);
  box-shadow: 0 2px 6px rgba(var(--brand-color-rgb), 0.1);
  border: 1px solid rgba(var(--brand-color-rgb), 0.35);
}

.nav-item--active:hover {
  background: rgba(var(--brand-color-rgb), 0.18);
}

.nav-item--active :deep(.v-icon),
.nav-item--active .nav-text {
  color: var(--brand-color-dark) !important;
}

.nav-text {
  color: rgb(var(--brand-color-rgb));
  font-size: 13px !important;
}

.nav-chip--glow {
  animation: comingSoonGlow 1.2s ease-in-out infinite !important;
}

@keyframes comingSoonGlow {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(243, 156, 18, 0.3);
    filter: brightness(1);
  }
  50% {
    box-shadow: 0 0 0 3px rgba(243, 156, 18, 0.35), 0 0 14px rgba(243, 156, 18, 0.55);
    filter: brightness(1.12);
  }
}

.sidebar-icon {
  background-color: transparent !important;
  border: 1px solid rgba(var(--brand-color-rgb), 0.5) !important;
  color: rgba(var(--brand-color-rgb), 0.9) !important;
}

.sidebar-icon :deep(.v-icon) {
  color: inherit !important;
}

.sidebar-icon:hover {
  background-color: rgba(var(--brand-color-rgb), 0.08) !important;
  border-color: rgba(var(--brand-color-rgb), 0.5) !important;
}

.b-color {
  border-block-start-color: rgba(var(--brand-color-rgb), 0.15) !important;
}

.sidebar-footer {
  margin-top: auto;
}
:deep(.v-responsive) {
  flex: none !important;
}
</style>
